import React, { useState, useEffect, useRef } from "react";
import axios from "utils/axios";
import { FaFileUpload } from "react-icons/fa";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import { showToast } from "components/ui/toast";
import VideoContainer from "components/upload/VideoContainer";

const CHUNK_SIZE = 10 * 1024 * 1024;
let isPaused = false;

const extractS3KeyFromUrl = (url) => {
  if (!url) return null;
  const match = url.match(/\/([^/]+)$/);
  return match ? match[1] : null;
};

const VideoUpload = ({ eventProp, folderPath, type }) => {
  useEffect(() => {
    const online = () => {
      isPaused = false;
    };
    const offline = () => {
      isPaused = true;
    };
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  }, []);

  const [files, setFiles] = useState(
    type === "Edited"
      ? eventProp?.editedVideos || []
      : eventProp?.rawVideos || []
  );
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedSizes, setUploadedSizes] = useState({});
  const [totalSizes, setTotalSizes] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const previousChunkBytes = useRef({});

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + " " + sizes[i]
    );
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length === 0) return;
    const newFiles = selected.map((file) => ({
      id: `${file.name}-${Date.now()}`,
      file,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
    setUploadProgress((prev) => {
      const n = { ...prev };
      newFiles.forEach((f) => {
        n[f.id] = 0;
      });
      return n;
    });
    setUploadedSizes((prev) => {
      const n = { ...prev };
      newFiles.forEach((f) => {
        n[f.id] = 0;
      });
      return n;
    });
    setTotalSizes((prev) => {
      const n = { ...prev };
      newFiles.forEach((f) => {
        n[f.id] = f.file.size;
      });
      return n;
    });
    setUploadQueue((prev) => [...prev, ...newFiles]);
    e.target.value = "";
  };

  useEffect(() => {
    if (uploadQueue.length > 0 && !isUploading) uploadNextVideo();
  }, [uploadQueue, isUploading]);

  const uploadNextVideo = async () => {
    if (uploadQueue.length === 0) return;
    setIsUploading(true);
    try {
      await uploadSingleVideo(uploadQueue[0]);
    } catch (error) {
      console.error("Error:", error);
    }
    setUploadQueue((prev) => prev.slice(1));
    setIsUploading(false);
  };

  const uploadSingleVideo = async (fileObj) => {
    const { id, file } = fileObj;
    previousChunkBytes.current[id] = 0;
    const { data: init } = await axios.post(
      "/admin/prepareMarketing/video/initiate",
      {
        fileName: file.name,
        fileType: file.type,
        propertyId: eventProp?.propertyId?._id,
        folderPath,
      }
    );
    const { uploadId, key } = init;
    const parts = [];
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
      while (isPaused) await new Promise((r) => setTimeout(r, 1000));
      const start = (partNumber - 1) * CHUNK_SIZE;
      const chunk = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
      const formData = new FormData();
      formData.append("file", chunk);
      formData.append("uploadId", uploadId);
      formData.append("key", key);
      formData.append("partNumber", partNumber);
      const { data: partRes } = await axios.post(
        "/admin/prepareMarketing/video/upload-part",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      parts.push({ PartNumber: partNumber, ETag: partRes.ETag });
      previousChunkBytes.current[id] += chunk.size;
      const uploaded = previousChunkBytes.current[id];
      setUploadedSizes((prev) => ({ ...prev, [id]: uploaded }));
      setUploadProgress((prev) => ({
        ...prev,
        [id]: Math.round((uploaded / file.size) * 100),
      }));
    }
    await axios.post("/admin/prepareMarketing/video/complete", {
      uploadId,
      videoName: file.name,
      videoSize: file.size,
      key,
      parts,
      eventId: eventProp?._id,
      propertyId: eventProp?.propertyId?._id,
      field: type === "Raw" ? "rawVideos" : "editedVideos",
      address: eventProp?.propertyId?.address,
    });
    setFiles((prev) =>
      prev.map((f) =>
        f.id === id ? { ...f, file: { ...f.file, s3Key: key } } : f
      )
    );
    setUploadProgress((prev) => ({ ...prev, [id]: 100 }));
  };

  const handleDelete = async (fileId) => {
    const file = files.find((f) => f.id === fileId);
    if (!file?.file?.s3Key && !file?.url) {
      removeFileFromStates(fileId);
      return;
    }
    try {
      const res = await axios.post("/admin/prepareMarketing/deleteS3File", {
        key: file?.file?.s3Key || extractS3KeyFromUrl(file?.url),
        name: file?.file?.name || file?.name,
        eventId: eventProp?._id,
        propertyId: eventProp?.propertyId?._id,
        field: type === "Raw" ? "rawVideos" : "editedVideos",
      });
      removeFileFromStates(fileId);
      if (res.data.success) showToast("success", "File deleted successfully");
    } catch (error) {
      showToast("error", "Failed to delete file");
    }
  };

  const removeFileFromStates = (fileId) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
    setUploadQueue((prev) => prev.filter((f) => f.id !== fileId));
    setUploadProgress((prev) => {
      const n = { ...prev };
      delete n[fileId];
      return n;
    });
    setUploadedSizes((prev) => {
      const n = { ...prev };
      delete n[fileId];
      return n;
    });
    setTotalSizes((prev) => {
      const n = { ...prev };
      delete n[fileId];
      return n;
    });
  };

  const [reviewConfirm, setReviewConfirm] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);

  const sendToReview = async () => {
    try {
      setReviewLoading(true);
      const res = await axios.put(
        `/admin/prepareMarketing/${eventProp?.propertyId?._id}`,
        { video: "Review", eventId: eventProp?._id }
      );
      if (res.data.success) {
        showToast("success", "Send to review successfully");
        setReviewConfirm(false);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setReviewLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div>
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            className="hidden"
            id={`video-upload-${type.toLowerCase()}-${eventProp._id}`}
            accept="video/*"
          />
          <label
            htmlFor={`video-upload-${type.toLowerCase()}-${eventProp._id}`}
            className="flex items-center black-button cursor-pointer"
          >
            <FaFileUpload className="mr-1.5" /> Upload
          </label>
        </div>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {files?.map((fileObj, index) => (
          <VideoContainer
            key={fileObj.id || index}
            video={fileObj.file || fileObj}
            onDelete={() => handleDelete(fileObj.id)}
            viewMode="grid"
            progress={uploadProgress[fileObj.id] || 0}
            showDelete={eventProp?.status !== "completed"}
            uploadedSize={formatBytes(uploadedSizes[fileObj.id] || 0)}
            totalSize={formatBytes(totalSizes[fileObj.id] || 0)}
            eventId={eventProp?._id}
            propertyId={eventProp?.propertyId?._id}
          />
        ))}
      </div>
      <Modal
        isOpen={reviewConfirm}
        onClose={() => setReviewConfirm(false)}
        title="Send to Review Confirmation"
      >
        <div className="flex flex-col space-y-4">
          <p>Are you sure you want to proceed?</p>
          <p className="italic">
            <strong>Note:</strong> Once sent, it cannot be undone.
          </p>
          <div className="w-full flex gap-2 justify-end">
            <Button
              onClick={() => setReviewConfirm(false)}
              className="gray-button"
            >
              No
            </Button>
            <Button
              onClick={sendToReview}
              className="black-button"
              loading={reviewLoading}
            >
              Yes
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default VideoUpload;
